/**
 * SourceItem - Dark theme source card with colored icons
 *
 * Features:
 * - Colored icon based on source type (PDF=blue, URL=orange, etc.)
 * - Dark glass card with subtle border
 * - Checkbox on the right
 * - Excluded items are grayed out with dashed border
 */

import { useEffect, useRef } from 'react';
import { KnowledgeDocument } from '@deep-agents/shared-types';

// === ICONS ===
const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const TextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="17" y1="10" x2="3" y2="10" />
    <line x1="21" y1="6" x2="3" y2="6" />
    <line x1="21" y1="14" x2="3" y2="14" />
    <line x1="17" y1="18" x2="3" y2="18" />
  </svg>
);

const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const AudioIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const LoadingIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10">
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);

// === HELPERS ===

// Format file size
function formatBytes(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

// Get icon and color theme based on source type and mime type
function getSourceTheme(doc: KnowledgeDocument): {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
  shadowColor: string;
} {
  const mimeType = doc.mimeType || '';

  // PDF files - blue theme
  if (mimeType.includes('pdf') || doc.title.toLowerCase().endsWith('.pdf')) {
    return {
      icon: <FileIcon />,
      color: '#60a5fa',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.2)',
      shadowColor: 'rgba(59, 130, 246, 0.05)',
    };
  }

  // Audio files - purple theme
  if (doc.sourceType === 'audio' || mimeType.includes('audio')) {
    return {
      icon: <AudioIcon />,
      color: '#a78bfa',
      bgColor: 'rgba(167, 139, 250, 0.1)',
      borderColor: 'rgba(167, 139, 250, 0.2)',
      shadowColor: 'rgba(167, 139, 250, 0.05)',
    };
  }

  // URLs - orange/amber theme
  if (doc.sourceType === 'url') {
    return {
      icon: <GlobeIcon />,
      color: '#fb923c',
      bgColor: 'rgba(251, 146, 60, 0.1)',
      borderColor: 'rgba(251, 146, 60, 0.2)',
      shadowColor: 'rgba(251, 146, 60, 0.05)',
    };
  }

  // Text input - green theme
  if (doc.sourceType === 'text') {
    return {
      icon: <TextIcon />,
      color: '#34d399',
      bgColor: 'rgba(52, 211, 153, 0.1)',
      borderColor: 'rgba(52, 211, 153, 0.2)',
      shadowColor: 'rgba(52, 211, 153, 0.05)',
    };
  }

  // Default upload - indigo theme
  return {
    icon: <FileIcon />,
    color: '#818cf8',
    bgColor: 'rgba(129, 140, 248, 0.1)',
    borderColor: 'rgba(129, 140, 248, 0.2)',
    shadowColor: 'rgba(129, 140, 248, 0.05)',
  };
}

// Get subtitle text
function getSubtitle(doc: KnowledgeDocument): string {
  const parts: string[] = [];

  // Source type label
  switch (doc.sourceType) {
    case 'upload':
      parts.push('Uploaded File');
      break;
    case 'url':
      parts.push('Web Page');
      break;
    case 'text':
      parts.push('Text Input');
      break;
    case 'audio':
      parts.push('Audio File');
      break;
    default:
      parts.push('Document');
  }

  // File size if available
  if (doc.sizeBytes) {
    parts.push(formatBytes(doc.sizeBytes));
  }

  return parts.join(' • ');
}

// === COMPONENT ===
interface SourceItemProps {
  document: KnowledgeDocument;
  isSelected: boolean;
  onSelectionChange: (selected: boolean) => void;
  /** Whether this item is highlighted (e.g., from citation click) */
  isHighlighted?: boolean;
}

export function SourceItem({ document, isSelected, onSelectionChange, isHighlighted }: SourceItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);

  // Auto-scroll into view when highlighted
  useEffect(() => {
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);
  const theme = getSourceTheme(document);
  const isReady = document.status === 'ready';
  const isProcessing = document.status === 'processing';
  const isFailed = document.status === 'failed';

  return (
    <div
      ref={itemRef}
      className={`source-item ${isSelected ? 'selected' : 'excluded'} ${isFailed ? 'failed' : ''} ${isHighlighted ? 'highlighted' : ''}`}
      onClick={() => isReady && onSelectionChange(!isSelected)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          isReady && onSelectionChange(!isSelected);
        }
      }}
    >
      {/* Colored Icon */}
      <div
        className="source-icon"
        style={{
          background: isSelected ? theme.bgColor : 'rgba(51, 65, 85, 0.3)',
          borderColor: isSelected ? theme.borderColor : 'rgba(255, 255, 255, 0.05)',
          color: isSelected ? theme.color : 'rgba(148, 163, 184, 0.5)',
          boxShadow: isSelected ? `0 4px 12px ${theme.shadowColor}` : 'none',
        }}
      >
        {isProcessing ? <LoadingIcon /> : theme.icon}
      </div>

      {/* Content */}
      <div className="source-content">
        <span className="source-title">{document.title}</span>
        <span className="source-subtitle">
          {isProcessing ? 'Processing...' : isFailed ? 'Failed to process' : getSubtitle(document)}
        </span>
      </div>

      {/* Checkbox */}
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelectionChange(e.target.checked);
        }}
        disabled={!isReady}
        className="source-checkbox"
        onClick={(e) => e.stopPropagation()}
      />

      <style>{`
        .source-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .source-item:hover {
          background: rgba(255, 255, 255, 0.04);
        }

        .source-item.selected {
          /* Active state - normal appearance */
        }

        .source-item.excluded {
          opacity: 0.4;
          filter: grayscale(0.5);
          background: transparent;
          border-style: dashed;
        }

        .source-item.excluded:hover {
          opacity: 0.6;
        }

        .source-item.failed {
          border-color: rgba(239, 68, 68, 0.3);
        }

        .source-item.highlighted {
          animation: highlightPulse 2s ease-in-out;
          border-color: rgba(99, 102, 241, 0.6);
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3), 0 4px 12px rgba(99, 102, 241, 0.2);
        }

        @keyframes highlightPulse {
          0% {
            border-color: rgba(99, 102, 241, 0.8);
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.4), 0 4px 16px rgba(99, 102, 241, 0.3);
          }
          50% {
            border-color: rgba(99, 102, 241, 0.6);
            box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3), 0 4px 12px rgba(99, 102, 241, 0.2);
          }
          100% {
            border-color: rgba(255, 255, 255, 0.05);
            box-shadow: none;
          }
        }

        .source-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid;
          transition: all 0.15s ease;
        }

        .source-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .source-title {
          font-size: 14px;
          font-weight: 500;
          color: rgba(226, 232, 240, 0.9);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .source-item.excluded .source-title {
          color: rgba(148, 163, 184, 0.6);
        }

        .source-subtitle {
          font-size: 11px;
          color: rgba(100, 116, 139, 0.7);
        }

        .source-item.excluded .source-subtitle {
          color: rgba(100, 116, 139, 0.4);
        }

        .source-checkbox {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          cursor: pointer;
          accent-color: #6366f1;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }

        .source-checkbox:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .source-checkbox:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
        }
      `}</style>
    </div>
  );
}
